import React, { useMemo } from 'react';
import { View } from 'react-native';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import ServiceFlowTimelineRow from './ServiceFlowTimelineRow';
import { deriveFlowOffsets, formatInstanceTimeLabel, formatOffsetLabel } from '@/utils/serviceFlow';
import { serviceFlowStyles, FLOW_RAIL_LEFT } from './serviceFlowStyles';

/**
 * Service Flow v2 timeline — the primary view in BOTH template and occurrence
 * contexts (spec §3.4/§3.5). Continuous rail + cue markers with the time cell
 * immediately right of the rail so it doesn't consume a dedicated gutter.
 *
 * Presentation-only: parents supply items and callbacks. Only items flagged
 * `showTime` get a time label — relative offset labels (`10 min before`) in
 * `template` context, real local times resolved in the ORGANIZATION timezone
 * in `instance` context (spec §7.1). Times are derived sequentially from
 * durations; explicit offsets anchor the sequence.
 *
 * @param {Object} props
 * @param {Array} props.items - normalized typed flow items
 * @param {'template'|'instance'} [props.context='instance']
 * @param {string} [props.occurrenceStartISO] - occurrence startTime (instance context)
 * @param {string} [props.timezone] - organization timezone (instance context)
 * @param {React.ReactNode} [props.header] - optional header/actions row
 * @param {React.ReactNode} [props.emptyState] - override for the empty state
 * @param {string|null} [props.selectedItemID]
 * @param {Function} [props.onPressItem] - row press (opens editor)
 * @param {Function} [props.onEditItem]
 * @param {Function} [props.onDuplicateItem]
 * @param {Function} [props.onRemoveItem]
 * @param {Function} [props.onMoveItem] - (item, direction) accessible reorder
 * @param {Function} [props.renderItemBody] - (item) => ReactNode extra row body
 * @param {boolean} [props.canEdit]
 * @param {boolean} [props.compact] - narrower time cell
 */
const ServiceFlowTimeline = ({
    items = [],
    context = 'instance',
    occurrenceStartISO = null,
    allDay = false,
    timezone = 'UTC',
    header = null,
    emptyState = null,
    selectedItemID = null,
    onPressItem = null,
    onEditItem = null,
    onDuplicateItem = null,
    onRemoveItem = null,
    onMoveItem = null,
    renderItemBody = null,
    canEdit = false,
    compact = false,
}) => {
    const sortedItems = useMemo(
        () => [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
        [items]
    );

    // Derived sequential start times — shown only for items whose showTime
    // flag is on. Explicit (legacy) offsets win; otherwise the item starts
    // where the preceding item's duration ends.
    const derivedOffsets = useMemo(() => deriveFlowOffsets(sortedItems), [sortedItems]);

    const labelFor = (item) => {
        if (!item?.showTime) return { label: null, dayOffset: 0 };
        const offset = Number.isInteger(item.startOffsetMinutes)
            ? item.startOffsetMinutes
            : derivedOffsets.get(item.id);
        if (offset === null || offset === undefined) return { label: null, dayOffset: 0 };
        if (context === 'template') {
            return { label: formatOffsetLabel(offset), dayOffset: 0 };
        }
        const { label, dayOffset } = formatInstanceTimeLabel({
            item: { ...item, startOffsetMinutes: offset, _legacyTime: null },
            occurrenceStartISO,
            timezone,
            allDay,
        });
        // No placeholder copy — a label that cannot be resolved is omitted.
        return { label: label === 'Time not set' ? null : label, dayOffset };
    };

    return (
        <View testID="service-flow-timeline">
            {header}

            {sortedItems.length === 0 ? (
                emptyState || <EmptyListPlaceholder message="No service flow items yet" />
            ) : (
                <View style={serviceFlowStyles.timeline}>
                    {/* Continuous rail behind all rows */}
                    <View style={[serviceFlowStyles.rail, { left: FLOW_RAIL_LEFT }]} />

                    {sortedItems.map((item, index) => {
                        const { label, dayOffset } = labelFor(item);
                        return (
                            <ServiceFlowTimelineRow
                                key={item.id || index}
                                item={item}
                                timeLabel={label}
                                dayOffset={dayOffset}
                                compact={compact}
                                selected={selectedItemID === item.id}
                                canEdit={canEdit}
                                isFirst={index === 0}
                                isLast={index === sortedItems.length - 1}
                                onPress={onPressItem}
                                onEdit={canEdit ? onEditItem : null}
                                onDuplicate={canEdit ? onDuplicateItem : null}
                                onRemove={canEdit ? onRemoveItem : null}
                                onMove={canEdit ? onMoveItem : null}
                            >
                                {renderItemBody ? renderItemBody(item) : null}
                            </ServiceFlowTimelineRow>
                        );
                    })}
                </View>
            )}
        </View>
    );
};

export default ServiceFlowTimeline;
